/**
 * The unit a job / estimate form treats as "derived" for the selected job type
 * — the reference point that separates re-picking the inherited value (no
 * override, no reason) from a genuine manual override.
 *
 * The server resolves a multi-unit job type per record from the customer's
 * segment, the service address and the technician; the form cannot run that,
 * so it anchors on what it CAN know:
 *   - an edited record whose unit was DERIVED by the server → that unit (the
 *     resolver's answer for this record), ONLY while the job type still lists
 *     it — a unit the type no longer carries is not "derived" any more, and
 *     treating it so would make an unrelated edit silently unpin/re-derive;
 *   - a job type with exactly one unit → that unit;
 *   - otherwise nothing: any explicit pick is an override, so a dispatcher can
 *     correct the resolver within the job type's own candidate set.
 *
 * A CHOSEN unit is never "derived", whichever channel made the choice —
 * MANUAL_OVERRIDE (a dispatcher's reassignment) or SELECTED (the create form's
 * up-front pick). Reporting a SELECTED unit as derived is what let a routine
 * edit destroy the pick: the estimate form sends `businessUnitId: null` when
 * the picker still shows the derived value — "nothing is pinned here" — and
 * `EstimatesService.update()` answers an explicit null by RE-DERIVING. On a
 * still-empty draft that write goes through, so saving with the business-unit
 * field untouched walked a human's choice back onto whatever the resolver
 * ladder returned, a different unit whenever the job type lists more than one.
 */
/** The provenances that record a person naming the unit rather than a derivation. */
const CHOSEN_BUSINESS_UNIT_SOURCES = new Set(['MANUAL_OVERRIDE', 'SELECTED']);

export function deriveBusinessUnitId({
  candidateIds,
  persistedBusinessUnitId = null,
  persistedBusinessUnitSource = null,
}: {
  candidateIds: string[];
  persistedBusinessUnitId?: string | null;
  persistedBusinessUnitSource?: string | null;
}): string | null {
  if (
    persistedBusinessUnitId &&
    !CHOSEN_BUSINESS_UNIT_SOURCES.has(persistedBusinessUnitSource ?? '') &&
    candidateIds.includes(persistedBusinessUnitId)
  ) {
    return persistedBusinessUnitId;
  }
  return candidateIds.length === 1 ? candidateIds[0] : null;
}
